import cn from "classnames";
import Image, { type ImageProps } from "@/components/common/Image";
import {
  AspectRatio,
  ImageSize,
  ImageVariant,
  MediaPosition,
} from "@/constants/media";
import { getAspectRatioClassName } from "@/utils/style/getAspectRatioClassName";
import type { ExtendedUploadedImage } from "@/types";
import s from "./Picture.module.scss";

export interface PictureProps extends ImageProps {
  variant?: ImageVariant;
  aspectRatio?: AspectRatio;
  size?: ImageSize;
  position?: MediaPosition;
  baseBorderRadius?: boolean;
  imageConfig?: ExtendedUploadedImage;
}

export default function Picture(props: PictureProps) {
  const {
    variant,
    size,
    position,
    baseBorderRadius,
    imageConfig,
    aspectRatio = AspectRatio.Auto,
    ...restProps
  } = props;

  function getWrapperClassName(imgAspectRatio = aspectRatio) {
    return cn(
      "informa-picture",
      s.wrapper,
      restProps.className,
      {
        [s.square]: variant === ImageVariant.Square,
        [s.circle]: variant === ImageVariant.Circle,
        [s.withBaseBorderRadius]: baseBorderRadius,
      },
      getAspectRatioClassName(imgAspectRatio),
    );
  }

  function getPictureClassName(
    imgAspectRatio = aspectRatio,
    imgSize = size,
    imgPosition = position,
  ) {
    return cn("informa-image", {
      [s.strictSize]: variant ?? imgAspectRatio !== AspectRatio.Auto,
      [s.contain]: imgSize === ImageSize.Contain,
      [s.cover]: imgSize === ImageSize.Cover,
      [s.fitLeft]: imgPosition === MediaPosition.Left,
      [s.fitCenter]: imgPosition === MediaPosition.Center,
      [s.fitRight]: imgPosition === MediaPosition.Right,
    });
  }

  if (imageConfig?.source?.path) {
    const {
      source,
      altText,
      aspectRatio: imgAspectRation,
      size: imgSize,
      position: imgPosition,
    } = imageConfig;

    return (
      <div className={getWrapperClassName(imgAspectRation)}>
        <Image
          {...restProps}
          src={source.path}
          alt={altText}
          className={getPictureClassName(imgAspectRation, imgSize, imgPosition)}
        />
      </div>
    );
  }

  if (!restProps.src) return null;

  return (
    <div className={getWrapperClassName()}>
      <Image alt="" {...restProps} className={getPictureClassName()} />
    </div>
  );
}
